import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Badge, Box, IconButton, Popover, Tooltip, Typography, styled } from '@mui/material';
import Iconify from '../../../components/Iconify';
import { piiProps } from '../../../components/DemoBlur';
import PinErrorSummary from '../../../components/leadPins/PinErrorSummary';
import { patientPath } from '../../../components/callPill/patientPath';
import useLeadPins from '../../../hooks/useLeadPins';
import { dispatch } from '../../../redux/store';
import { getLeadPins, pinnedByName, selectMyLeadPins, unpinLead } from '../../../redux/slices/leadPins';
import { getJourneyDot, getJourneyLabel } from '../../../utils/leadJourney';
import { parseApiDateTime } from '../../../utils/parseApiDateTime';
import { NOTIFICATION_PALETTE } from '../../../sections/notifications/notificationTypes';

// -----------------------------------------------------------------
// Top-bar "Pinned leads" popover. A practice-wide list of leads staff have
// parked part-way through handling. Visual language mirrors the frosted-glass
// NotificationPanel / NotificationCard so it reads as part of the same header
// family. Hidden entirely while the practice has no pins.
// -----------------------------------------------------------------

export const PINNED_SCOPE_STORAGE_KEY = 'pinnedLeads.scope';
// Refetch on window focus at most this often.
export const FOCUS_REFRESH_MS = 30 * 1000;
const PANEL_WIDTH = 380;

const readScope = () => {
    try {
        return localStorage.getItem(PINNED_SCOPE_STORAGE_KEY) === 'mine' ? 'mine' : 'all';
    } catch {
        return 'all';
    }
};

const writeScope = (scope) => {
    try {
        localStorage.setItem(PINNED_SCOPE_STORAGE_KEY, scope);
    } catch { /* storage unavailable — keep the in-memory choice */ }
};

// Compact relative time: "just now", "5m ago", "2h ago", "3d ago", "12 Oct".
// `created_at` arrives in DRF's day-first "DD-MM-YYYY HH:mm:ss.ffffffZ" (UK
// wall-clock, literal Z) — plain moment() misreads it month-first.
export const formatPinnedAgo = (iso, now = moment()) => {
    const at = parseApiDateTime(iso);
    if (!at) return '';
    const mins = Math.max(0, now.diff(at, 'minutes'));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return at.format('D MMM');
};

// ── Styled (glass) ──────────────────────────────────────────────

const PanelRoot = styled(Box)(() => ({
    width: PANEL_WIDTH,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'min(560px, calc(100vh - 96px))',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'rgba(240, 242, 245, 0.65)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
}));

const PanelHeader = styled(Box)(() => ({
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '16px 16px 12px',
    borderBottom: '0.5px solid rgba(0,0,0,0.06)',
}));

const Segmented = styled(Box)(() => ({
    display: 'inline-flex',
    padding: 2,
    borderRadius: 10,
    background: 'rgba(145,158,171,0.12)',
}));

const SegmentButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
    fontFamily: "'Public Sans', sans-serif",
    border: 'none',
    cursor: 'pointer',
    padding: '4px 10px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: active ? 700 : 500,
    color: active ? '#212B36' : '#919EAB',
    backgroundColor: active ? 'rgba(255,255,255,0.9)' : 'transparent',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
    transition: 'all 0.15s ease',
}));

const ScrollArea = styled(Box)(() => ({
    flex: 1,
    overflowY: 'auto',
    padding: '8px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(145,158,171,0.2)', borderRadius: 2 },
}));

const GlassCard = styled(Box)(() => ({
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.82)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
    borderRadius: 18,
    border: '0.5px solid rgba(255,255,255,0.6)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04), inset 0 0.5px 0 rgba(255,255,255,0.6)',
    overflow: 'hidden',
}));

const SplitButton = styled('button')(() => ({
    flex: 1,
    fontFamily: "'Public Sans', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    padding: '10px 0',
    border: 'none',
    cursor: 'pointer',
    background: 'transparent',
    color: NOTIFICATION_PALETTE.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    '&:hover': { background: 'rgba(0,0,0,0.02)' },
}));

// ── Card ────────────────────────────────────────────────────────

export function PinnedLeadCard({ pin, practiceUserId, onOpen, onUnpin }) {
    const lead = pin?.lead_detail || {};
    const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed lead';
    const status = lead.latest_status;
    const by = pinnedByName(pin, practiceUserId);
    const ago = formatPinnedAgo(pin?.created_at);
    const accent = status ? getJourneyDot(status) : NOTIFICATION_PALETTE.primary.main;

    return (
        <GlassCard data-testid="pinned-lead-card">
            <Box sx={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 2, bgcolor: accent }} />
            <Box sx={{ display: 'flex', gap: '12px', p: '14px 12px 12px 18px' }}>
                <Box
                    sx={{
                        flexShrink: 0,
                        width: 38,
                        height: 38,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: NOTIFICATION_PALETTE.primary.lighter,
                        color: NOTIFICATION_PALETTE.primary.dark,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    }}
                >
                    <Iconify icon="mdi:pin" width={18} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '2px' }}>
                        <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.2px', minWidth: 0 }} {...piiProps}>
                            {name}
                        </Typography>
                        {status && (
                            <Box
                                component="span"
                                data-testid="pinned-lead-status"
                                sx={{
                                    flexShrink: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: 10.5,
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    bgcolor: NOTIFICATION_PALETTE.grey[200],
                                    px: '6px',
                                    py: '1px',
                                    borderRadius: '6px',
                                }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accent }} />
                                {getJourneyLabel(status)}
                            </Box>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Unpin">
                            <IconButton
                                size="small"
                                aria-label="Unpin lead"
                                data-testid="pinned-lead-unpin"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onUnpin(pin);
                                }}
                                sx={{ p: '3px', color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                            >
                                <Iconify icon="mdi:pin-off" width={16} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {pin?.reason ? (
                        <Typography
                            {...piiProps}
                            sx={{
                                fontSize: 13,
                                color: 'text.primary',
                                lineHeight: 1.45,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                wordBreak: 'break-word',
                            }}
                        >
                            {pin.reason}
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic' }}>No note</Typography>
                    )}
                    <PinErrorSummary pin={pin} />
                    <Typography sx={{ mt: '4px', fontSize: 12, color: 'text.disabled', fontWeight: 500 }}>
                        {`Pinned by ${by}${ago ? ` · ${ago}` : ''}`}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ borderTop: '0.5px solid rgba(0,0,0,0.05)', display: 'flex' }}>
                <SplitButton type="button" onClick={() => onOpen(pin)}>
                    Open lead
                    <Iconify icon="eva:arrow-ios-forward-fill" width={14} />
                </SplitButton>
            </Box>
        </GlassCard>
    );
}

PinnedLeadCard.propTypes = {
    pin: PropTypes.object.isRequired,
    onOpen: PropTypes.func.isRequired,
    onUnpin: PropTypes.func.isRequired,
    practiceUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PinnedLeadCard.defaultProps = {
    practiceUserId: '',
};

// ── Popover ─────────────────────────────────────────────────────

export default function PinnedLeadsPopover() {
    const navigate = useNavigate();
    const { practiceId, pins, practiceUserId } = useLeadPins();
    const anchorRef = useRef(null);
    const lastFetchRef = useRef(0);
    const [open, setOpen] = useState(false);
    const [scope, setScope] = useState(readScope);

    // The hook's initial load counts as a fetch for the focus throttle.
    useEffect(() => {
        lastFetchRef.current = Date.now();
    }, [practiceId]);

    // Refresh when the tab regains focus, at most once per FOCUS_REFRESH_MS —
    // colleagues pin and unpin from their own screens.
    useEffect(() => {
        if (!practiceId) return undefined;
        const onFocus = () => {
            const now = Date.now();
            if (now - lastFetchRef.current < FOCUS_REFRESH_MS) return;
            lastFetchRef.current = now;
            dispatch(getLeadPins(practiceId));
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [practiceId]);

    const myPins = useMemo(() => selectMyLeadPins(pins, practiceUserId), [pins, practiceUserId]);
    const visible = scope === 'mine' ? myPins : pins;

    const changeScope = useCallback((next) => {
        setScope(next);
        writeScope(next);
    }, []);

    const handleOpenLead = useCallback((pin) => {
        setOpen(false);
        if (practiceId && pin?.lead) navigate(patientPath(practiceId, pin.lead));
    }, [navigate, practiceId]);

    const handleUnpin = useCallback((pin) => {
        if (practiceId && pin?.id) dispatch(unpinLead(practiceId, pin.id));
    }, [practiceId]);

    // Close the popover if the last pin goes (the trigger is about to unmount).
    useEffect(() => {
        if (!pins.length) setOpen(false);
    }, [pins.length]);

    if (!pins.length) return null;

    return (
        <>
            <Tooltip title="Pinned leads">
                <IconButton
                    ref={anchorRef}
                    aria-label="Pinned leads"
                    data-testid="pinned-leads-button"
                    onClick={() => setOpen(true)}
                >
                    <Badge badgeContent={pins.length} color="primary" max={99}>
                        <Iconify icon="mdi:pin" width={22} />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            borderRadius: '18px',
                            boxShadow: '0 20px 40px -4px rgba(145,158,171,0.24), 0 0 2px 0 rgba(145,158,171,0.24)',
                            overflow: 'hidden',
                            border: '0.5px solid rgba(255,255,255,0.4)',
                        },
                    },
                }}
            >
                <PanelRoot data-testid="pinned-leads-panel">
                    <PanelHeader>
                        <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.3px' }}>
                            Pinned leads
                        </Typography>
                        <Segmented role="group" aria-label="Filter pinned leads">
                            <SegmentButton type="button" active={scope === 'all' ? 1 : 0} aria-pressed={scope === 'all'} onClick={() => changeScope('all')}>
                                All pins
                            </SegmentButton>
                            <SegmentButton type="button" active={scope === 'mine' ? 1 : 0} aria-pressed={scope === 'mine'} onClick={() => changeScope('mine')}>
                                My pins
                            </SegmentButton>
                        </Segmented>
                    </PanelHeader>
                    <ScrollArea>
                        {visible.length ? visible.map((pin) => (
                            <PinnedLeadCard
                                key={pin.id}
                                pin={pin}
                                practiceUserId={practiceUserId}
                                onOpen={handleOpenLead}
                                onUnpin={handleUnpin}
                            />
                        )) : (
                            <Box data-testid="pinned-leads-empty" sx={{ textAlign: 'center', py: 5, px: 2, color: '#919EAB' }}>
                                <Iconify icon="mdi:pin-outline" width={28} sx={{ color: '#C4CDD5', mb: 1 }} />
                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>
                                    You haven&apos;t pinned any leads
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
                                    Pin a lead from its detail page to come back to it later.
                                </Typography>
                            </Box>
                        )}
                    </ScrollArea>
                </PanelRoot>
            </Popover>
        </>
    );
}

